/**
 * Startup environment variable validator.
 *
 * Called in main.tsx BEFORE React renders. Catches missing or
 * malformed critical config so devs see an immediate, actionable
 * error instead of a cryptic runtime failure minutes later.
 *
 * • Dev mode  → throws Error (fail-fast)
 * • Prod mode → console.error (avoids blank screen for end-users)
 */

interface EnvRule {
  key: string;
  /** Optional prefix the value MUST start with. */
  prefix?: string;
  /** Human-readable reason shown when the prefix check fails. */
  prefixReason?: string;
}

const REQUIRED_VARS: EnvRule[] = [
  {
    key: "VITE_SUPABASE_URL",
    prefix: "https://",
    prefixReason: "Supabase URL must use HTTPS",
  },
  { key: "VITE_SUPABASE_ANON_KEY" },
  {
    key: "VITE_STRIPE_PUBLISHABLE_KEY",
    prefix: "pk_",
    prefixReason:
      'Stripe publishable key must start with "pk_" — if it starts with "sk_" you are leaking your SECRET key to the client bundle',
  },
  { key: "VITE_GOOGLE_MAPS_API_KEY" },
];

export function validateEnv(): void {
  const issues: string[] = [];

  for (const rule of REQUIRED_VARS) {
    const value = import.meta.env[rule.key] as string | undefined;

    if (!value || value.trim() === "") {
      issues.push(`Missing or empty: ${rule.key}`);
      continue; // skip prefix check — nothing to check
    }

    // A local Supabase stack (supabase start) serves plain HTTP on loopback —
    // allow it in every mode so the app can be pointed at a local database
    // for rehearsals without weakening the HTTPS rule for real hosts.
    const isLoopbackHttp =
      rule.prefix === "https://" &&
      /^http:\/\/(127\.0\.0\.1|localhost)(:\d+)?(\/|$)/.test(value);

    if (rule.prefix && !value.startsWith(rule.prefix) && !isLoopbackHttp) {
      issues.push(`Invalid ${rule.key}: ${rule.prefixReason ?? `must start with "${rule.prefix}"`}`);
    }
  }

  if (issues.length === 0) return;

  const message = [
    `[FairPath] ${issues.length} environment variable issue(s):`,
    ...issues.map((i) => `  • ${i}`),
    "",
    "Check your .env.local file and restart the dev server.",
  ].join("\n");

  if (import.meta.env.DEV) {
    throw new Error(message);
  }

  // Production: log loudly but don't crash the page
  console.error(message);
}
